import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ClipboardList, ChevronDown, ChevronRight, CheckCircle2, Circle,
    User, Calendar, Plus, Home, Zap, Layers, Cable, MapPin, StickyNote,
    ShieldCheck, MessageSquare, FileCheck2, AlertCircle,
} from "lucide-react";

import { api } from "@/lib/api";
import OperationsSurveyReview from "@/components/operations/OperationsSurveyReview";

/**
 * DEV-010A · Site Survey shell for the Operations project workspace.
 *
 * READ-ONLY summary of the survey lifecycle + section completion so Ops
 * can see at a glance whether the Engineer's survey is under way,
 * submitted, or approved. Full Ops review + approval flow (Accept /
 * Return for clarification / approved installation specification)
 * belongs to DEV-010C — this shell intentionally does NOT expose those
 * actions yet.
 *
 * Renders as its own card between the Customer contact block and the
 * Delivery & site logistics section (matches Ops workspace conventions).
 */

const STATUS_META = {
    not_started: { label: "Not started",             dot: "#94A3B8", bg: "#F1F5F9", fg: "#334155" },
    in_progress: { label: "In progress",             dot: "#F59E0B", bg: "#FEF3C7", fg: "#B45309" },
    submitted:   { label: "Submitted for review",    dot: "#3B82F6", bg: "#EFF6FF", fg: "#1D4ED8" },
    returned_for_clarification: { label: "Returned for clarification", dot: "#EF4444", bg: "#FEF2F2", fg: "#B91C1C" },
    // Legacy alias for pre-DEV-010C surveys that stored the old status string.
    returned:    { label: "Returned for clarification", dot: "#EF4444", bg: "#FEF2F2", fg: "#B91C1C" },
    approved:    { label: "Approved",                dot: "#3E9E2E", bg: "#F1F8EC", fg: "#166534" },
};

const SECTIONS = [
    { key: "additional_options",  icon: Layers,       label: "Additional options",   doneWhen: (s) => (s?.additional_options?.selected?.length || 0) > 0 },
    { key: "electrical",          icon: Zap,          label: "Electrical connection",doneWhen: (s) => Object.values(s?.electrical || {}).some(v => v !== null && v !== undefined && v !== "") },
    { key: "roof_elevations",     icon: Home,         label: "Roof / elevations",    doneWhen: (s) => (s?.elevations?.length || 0) > 0 },
    { key: "panel_installation",  icon: Layers,       label: "Panel installation",   doneWhen: (s) => Object.values(s?.panel_installation || {}).some(v => v !== null && v !== undefined && v !== "") },
    { key: "cable_conduit",       icon: Cable,        label: "Cable & conduit",      doneWhen: (s) => Object.values(s?.cable_conduit || {}).some(v => v !== null && v !== undefined && v !== "") },
    { key: "equipment_location",  icon: MapPin,       label: "Equipment location",   doneWhen: (s) => Object.values(s?.equipment_location || {}).some(v => v !== null && v !== undefined && v !== "") },
    { key: "additional_notes",    icon: StickyNote,   label: "Additional notes",     doneWhen: (s) => Boolean(s?.additional_notes?.text) },
];

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function SiteSurveySection({ project, engineersById = {}, reviewSurveyId = null, onProjectChanged = null }) {
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const [showReview, setShowReview] = useState(false);
    const [spec, setSpec] = useState(null);
    const [autoOpenedFor, setAutoOpenedFor] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const s = await api.getProjectSurvey(project.id);
            setSurvey(s);
        } catch (e) {
            if (e?.response?.status === 404) setSurvey(null);
            else setError(e?.response?.data?.detail || "Could not load survey");
        } finally {
            setLoading(false);
        }
    }, [project.id]);

    useEffect(() => { load(); }, [load]);

    // DEV-010D · Deep-link: when the caller (Ops notification handler)
    // passes a `reviewSurveyId` that matches the loaded survey, auto-open
    // the DEV-010C review drawer and expand the section so the operator
    // lands directly on the actionable content. We only auto-open once
    // per (project, survey) to avoid re-opening after the user closes.
    useEffect(() => {
        if (!reviewSurveyId || !survey) return;
        if (survey.id !== reviewSurveyId) return;
        if (autoOpenedFor === survey.id) return;
        setExpanded(true);
        setShowReview(true);
        setAutoOpenedFor(survey.id);
    }, [reviewSurveyId, survey, autoOpenedFor]);

    // DEV-010C · fetch the approved installation specification (if any).
    // UAT blocker fix: reset before every fetch (and clear on 404) so a
    // previously-selected project's spec cannot bleed into a newly created
    // project that legitimately has no approved specification.
    useEffect(() => {
        let cancelled = false;
        setSpec(null);
        (async () => {
            try {
                const s = await api.getApprovedSpecification(project.id);
                if (!cancelled) setSpec(s);
            } catch (e) {
                if (!cancelled) setSpec(null);
            }
        })();
        return () => { cancelled = true; };
    }, [project.id, survey?.status]);

    const create = async () => {
        setCreating(true);
        try {
            const s = await api.createOrOpenSurvey(project.id);
            setSurvey(s);
            setExpanded(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not create survey");
        } finally {
            setCreating(false);
        }
    };

    const status = STATUS_META[survey?.status] || STATUS_META.not_started;
    const done = useMemo(() => {
        if (!survey) return { count: 0, total: SECTIONS.length };
        const count = SECTIONS.filter((s) => s.doneWhen(survey)).length;
        return { count, total: SECTIONS.length };
    }, [survey]);

    const surveyorName = survey?.surveyor_engineer_id
        ? (engineersById[survey.surveyor_engineer_id]?.displayName || "Assigned")
        : "Unassigned";

    // DEV-010G · Clarification is outstanding when at least one
    // clarifications[] entry has no `resolved_at`. This is independent
    // of section completeness (which the tile also renders).
    const hasUnresolvedClarification = (survey?.clarifications || []).some(
        (c) => !c.resolved_at,
    );

    return (
        <section
            id="survey"
            data-testid="ops-projects-detail-survey"
            className="rounded-xl border border-[#E5E7EB] bg-white"
        >
            <header
                onClick={() => survey && setExpanded((v) => !v)}
                className={"px-5 py-4 flex items-center gap-3 border-b border-[#E5E7EB] " + (survey ? "cursor-pointer" : "")}
            >
                <span className="w-8 h-8 rounded-lg bg-[#F1F5F9] text-[#131B2F] flex items-center justify-center">
                    <ClipboardList size={16} strokeWidth={1.75} />
                </span>
                <div className="flex-1">
                    <div className="text-sm font-semibold text-[#131B2F]">Digital site survey</div>
                    <div className="text-xs text-[#4B5565] mt-0.5">
                        {loading ? "Loading…" :
                         !survey ? "Not started — no survey record for this project yet." :
                         `${done.count}/${done.total} sections have content · surveyor: ${surveyorName}`}
                    </div>
                    {/* DEV-010G · Surface workflow/review state on the
                        summary tile independently of section completeness
                        so operators see "Clarification outstanding"
                        without needing to open the review drawer. */}
                    {survey && hasUnresolvedClarification && (
                        <div
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[#7C2D12] bg-[#FFF7ED] border border-[#FED7AA] rounded-full px-2 py-0.5"
                            data-testid="survey-clarification-status"
                        >
                            <AlertCircle size={11} strokeWidth={2} />
                            Clarification outstanding
                        </div>
                    )}
                </div>
                {survey && (
                    <span
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full"
                        style={{ background: status.bg, color: status.fg }}
                        data-testid="survey-status-pill"
                    >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dot }} />
                        {status.label}
                    </span>
                )}
                {survey && ["submitted", "returned_for_clarification", "returned", "approved"].includes(survey.status) && (
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowReview(true); }}
                        data-testid="ops-review-survey-btn"
                        className="ml-2 inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-medium bg-[#131B2F] text-white hover:brightness-110"
                    >
                        <ShieldCheck size={12} strokeWidth={2} />
                        {survey.status === "approved" ? "View review" : "Review survey"}
                    </button>
                )}
                {!survey && !loading && (
                    <button
                        type="button"
                        onClick={create}
                        disabled={creating}
                        data-testid="survey-create"
                        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110 disabled:opacity-60"
                    >
                        <Plus size={14} strokeWidth={2.25} /> {creating ? "Creating…" : "Create survey"}
                    </button>
                )}
                {survey && (
                    <ChevronDown size={18} className={"text-[#4B5565] transition-transform " + (expanded ? "rotate-180" : "")} strokeWidth={1.75} />
                )}
            </header>

            {error && (
                <div className="px-5 py-3 text-sm text-[#B91C1C] bg-[#FEF2F2] border-b border-[#FEE2E2]" data-testid="survey-error">
                    {error}
                </div>
            )}

            {survey && expanded && (
                <div className="p-5 space-y-4">
                    {/* Header meta */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <MetaCell icon={User} label="Surveyor" value={surveyorName} />
                        <MetaCell icon={Calendar} label="Survey date" value={fmtDate(survey.survey_date)} />
                        <MetaCell icon={ClipboardList} label="Linked visit"
                            value={survey.appointment_id ? survey.appointment_id.slice(0, 8) + "…" : "—"} />
                    </div>

                    {/* Section list (read-only progress + brief peek) */}
                    <ul className="rounded-lg border border-[#F1F5F9] divide-y divide-[#F1F5F9] overflow-hidden" data-testid="survey-sections">
                        {SECTIONS.map((sec) => {
                            const Icon = sec.icon;
                            const doneFlag = sec.doneWhen(survey);
                            const peek = renderPeek(sec.key, survey);
                            return (
                                <li key={sec.key} className="px-4 py-3 flex items-start gap-3" data-testid={`survey-section-${sec.key}`}>
                                    <div className={"shrink-0 w-7 h-7 rounded-md flex items-center justify-center " + (doneFlag ? "bg-[#F1F8EC] text-[#166534]" : "bg-[#F1F5F9] text-[#4B5565]")}>
                                        {doneFlag ? <CheckCircle2 size={13} strokeWidth={2} /> : <Icon size={13} strokeWidth={1.75} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-[#131B2F] font-medium">{sec.label}</div>
                                        <div className="text-xs text-[#4B5565] mt-0.5 truncate">{peek || (doneFlag ? "Content captured" : "No survey data yet")}</div>
                                    </div>
                                    {doneFlag && <span className="text-[10px] text-[#166534] font-medium">Captured</span>}
                                </li>
                            );
                        })}
                    </ul>

                    {/* Proposed system snapshot (for later variance review — DEV-010C) */}
                    {survey.proposed_snapshot && (
                        <div className="rounded-lg border border-dashed border-[#E5E7EB] bg-[#FAFBFC] p-3" data-testid="survey-proposed-snapshot">
                            <div className="text-[10px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">Proposed system snapshot (immutable, captured at survey creation)</div>
                            <div className="text-sm text-[#131B2F] mt-1">{survey.proposed_snapshot.system_summary || "—"}</div>
                            {Array.isArray(survey.proposed_snapshot.equipment) && survey.proposed_snapshot.equipment.length > 0 && (
                                <ul className="text-xs text-[#4B5565] mt-1 space-y-0.5">
                                    {survey.proposed_snapshot.equipment.map((e, i) => (
                                        <li key={i} className="truncate">· {e.type}: {e.model} ({e.quantity})</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
            {spec && (
                <div className="px-5 py-4 border-t border-[#DCFCE7] bg-[#F7FBF4]" data-testid="approved-specification-card">
                    <div className="flex items-start gap-3">
                        <FileCheck2 size={20} className="text-[#166534] mt-0.5" strokeWidth={1.75} />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#131B2F]">
                                Approved installation specification (Version {spec.version})
                            </div>
                            <div className="text-xs text-[#4B5565] mt-0.5">
                                Approved by {spec.approved_by_name || "Ops"} · {fmtDate(spec.approved_at)}
                            </div>
                            <SpecFields spec={spec.specification} />
                            {spec.approval_note && (
                                <div className="text-xs text-[#4B5565] mt-2">Note: {spec.approval_note}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {showReview && survey && (
                <OperationsSurveyReview
                    surveyId={survey.id}
                    onClose={() => setShowReview(false)}
                    onLifecycleChange={() => { load(); }}
                    onProjectChanged={onProjectChanged}
                />
            )}
        </section>
    );
}

function SpecFields({ spec }) {
    if (!spec) return null;
    const rows = [
        ["Panel quantity", spec.panel_quantity],
        ["Panel installation method", spec.panel_installation_method],
        ["Rail colour", spec.rail_colour],
        ["Additional options", spec.additional_options],
        ["Inverter location", spec.inverter_location],
        ["Battery location", spec.battery_location],
    ];
    return (
        <ul className="mt-3 space-y-1.5 text-xs" data-testid="approved-spec-fields">
            {rows.map(([label, cell]) => {
                if (!cell || cell.value === null || cell.value === undefined || cell.value === "") return null;
                const src = cell.source === "ops_decision" ? "Ops decision" : cell.source === "proposed" ? "Proposed" : "Survey";
                const val = Array.isArray(cell.value) ? cell.value.join(", ") : String(cell.value);
                return (
                    <li key={label} className="flex items-baseline gap-2">
                        <span className="text-[#4B5565] w-40 shrink-0">{label}</span>
                        <span className="text-[#131B2F] font-medium">{val}</span>
                        <span className="text-[10px] uppercase tracking-[0.1em] text-[#166534] font-medium">{src}</span>
                    </li>
                );
            })}
        </ul>
    );
}

function renderPeek(sectionKey, survey) {
    switch (sectionKey) {
        case "additional_options": {
            const sel = survey.additional_options?.selected || [];
            if (!sel.length) return null;
            return sel.map(labelize).join(", ");
        }
        case "electrical": {
            const el = survey.electrical || {};
            const bits = [];
            if (el.connection_point) bits.push(`Connection: ${labelize(el.connection_point)}`);
            if (el.distance_meters != null) bits.push(`Distance: ${el.distance_meters}m`);
            if (el.surge_protection) bits.push(`Surge: ${labelize(el.surge_protection)}`);
            return bits.join(" · ");
        }
        case "roof_elevations": {
            const evs = survey.elevations || [];
            if (!evs.length) return null;
            return evs.map(e => `${e.name || e.orientation || "Elevation"} (${e.surveyed_panel_qty ?? "?"} panels)`).join(", ");
        }
        case "panel_installation": {
            const p = survey.panel_installation || {};
            const bits = [];
            if (p.method) bits.push(labelize(p.method));
            if (p.rail_colour) bits.push(`${labelize(p.rail_colour)} rails`);
            if (p.panel_quantity != null) bits.push(`${p.panel_quantity} panels`);
            return bits.join(" · ");
        }
        case "cable_conduit": {
            const c = survey.cable_conduit || {};
            const bits = [];
            if (c.cable_type) bits.push(labelize(c.cable_type));
            if (c.conduit_length_m != null) bits.push(`${c.conduit_length_m}m conduit`);
            if (c.copex_required) bits.push(`COPEX: ${labelize(c.copex_required)}`);
            return bits.join(" · ");
        }
        case "equipment_location": {
            const l = survey.equipment_location || {};
            const bits = [];
            if (l.proposed_inverter_location) bits.push(`Inverter: ${l.proposed_inverter_location}`);
            if (l.proposed_battery_location) bits.push(`Battery: ${l.proposed_battery_location}`);
            return bits.join(" · ");
        }
        case "additional_notes":
            return survey.additional_notes?.text || null;
        default:
            return null;
    }
}

function labelize(v) {
    return String(v || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function MetaCell({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-md bg-[#F5F5F7] text-[#131B2F] flex items-center justify-center shrink-0">
                <Icon size={12} strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">{label}</div>
                <div className="text-sm text-[#131B2F] truncate">{value}</div>
            </div>
        </div>
    );
}
